(() => {
  const prefersReducedMotion = () =>
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  // Breakpoint matches `$lg` in the SCSS (`base/components/grid.scss`). Above it the slider
  // is vertical with `slidesPerView: 1.5`; below it the slider is horizontal with
  // `slidesPerView: ~1.78` (375 px viewport ÷ 211 px target slide).
  const desktopMql = window.matchMedia("(min-width: 990px)");
  const SLIDES_PER_VIEW_DESKTOP = 1.5;
  const SLIDES_PER_VIEW_TABLET = 1.78;
  const SLIDES_PER_VIEW_MOBILE = 1.4;

  const initSwiper = (box) => {
    const el = box.querySelector(".vertical-slideshow__cards.swiper");
    if (!el) return null;
    const slides = el.querySelectorAll(".swiper-slide");
    // A single block renders as a static centered card (no `.swiper`), so we never reach here with
    // < 2 slides. The slide count is padded in Liquid so loop has enough duplicates on both sides.
    if (slides.length < 2) return null;

    // Mobile base config; the `breakpoints[990]` block overrides everything that differs on
    // desktop. Swiper handles the direction + slidesPerView switch internally when the
    // viewport crosses 990 px (it calls `changeDirection` under the hood and re-runs setTranslate),
    // so we don't need a manual matchMedia listener. The aspect of each slide comes from the CSS
    // `aspect-ratio` on the cards container (`--vs-cards-aspect-mobile` / `--vs-cards-aspect`).
    return new Swiper(el, {
      direction: "horizontal",
      slidesPerView: SLIDES_PER_VIEW_MOBILE,
      centeredSlides: true,
      spaceBetween: 16,
      speed: 600,
      grabCursor: true,
      loop: true,
      watchSlidesProgress: true,
      threshold: 5,
      breakpoints: {
        576: {
          direction: "horizontal",
          slidesPerView: SLIDES_PER_VIEW_TABLET,
          spaceBetween: 16,
        },
        990: {
          direction: "vertical",
          slidesPerView: SLIDES_PER_VIEW_DESKTOP,
          spaceBetween: 24,
        },
      },
    });
  };

  // Outside-the-wheel tilt — mirrors expo-slider's algorithm but flipped to lean outward.
  //
  // Important: in Swiper VERTICAL mode the sign of `slide.progress` is the opposite of
  // horizontal — the prev slide (above active) gets POSITIVE progress, and the next slide
  // (below active) gets NEGATIVE progress. Hence:
  //   prev (progress > 0): origin "center bottom" (near-active edge anchored), rotateX = +rotate
  //   next (progress < 0): origin "center top"    (near-active edge anchored), rotateX = -rotate
  // Both make the far edge recede in -Z (outside the cylinder). At progress = 0 rotate is 0,
  // so the origin switch at the active boundary is invisible.
  //
  // `setTransition` mirrors expo-slider so Swiper's slide-change duration drives the card's
  // transition too; without it the inline transforms would snap at the end of a swipe.
  const TILT_DEG = 20;
  const SCALE_AT_NEIGHBOUR = 0.85;

  const initEffect = (swiper) => {
    if (!swiper) return;
    // Respect the user's motion preference — skip the per-slide tilt entirely. Cards still
    // scroll, just without the 3D rotation/scale that the regular path applies on each progress
    // tick. The background wipe already has its own reduced-motion branch.
    if (prefersReducedMotion()) return;

    const setTranslate = () => {
      // Mobile uses a horizontal slider where a rotateX tilt makes no sense; skip the effect
      // and clear any leftover inline transforms from a previous desktop render. The tilt
      // rides on `.vertical-slideshow-card__inner` (NOT on the outer `<a>`) — keeping the
      // link flat means its hit-test isn't pulled into the slide's preserve-3d chain.
      if (!desktopMql.matches) {
        swiper.slides.forEach((slide) => {
          const inner = slide.querySelector(".vertical-slideshow-card__inner");
          if (!inner) return;
          if (inner.style.transform) inner.style.transform = "";
          if (inner.style.transformOrigin) inner.style.transformOrigin = "";
        });
        return;
      }
      swiper.slides.forEach((slide) => {
        const inner = slide.querySelector(".vertical-slideshow-card__inner");
        if (!inner) return;
        const progress = slide.progress || 0;
        const clamped = Math.max(-1, Math.min(1, progress));
        const absP = Math.abs(clamped);
        // Update origin only away from the active boundary — at progress ≈ 0 the rotation is
        // also 0 (identity transform), so the origin is visually irrelevant there. Resetting it
        // to "center center" each tick caused a snap because the next setTranslate call would
        // then animate around a different pivot.
        if (Math.abs(progress) > 0.01) {
          inner.style.transformOrigin =
            progress > 0 ? "center bottom" : "center top";
        }
        const scale = 1 - (1 - SCALE_AT_NEIGHBOUR) * absP;
        inner.style.transform = `rotateX(${
          clamped * TILT_DEG
        }deg) scale(${scale})`;
      });
    };

    // Swiper events pass the swiper instance as the FIRST arg to handlers; the actual emit
    // payload comes from arg 2 onwards. So for `setTransition`, the duration is the SECOND arg
    // — not the first. Reading it as the first turns `transition-duration` into `[object …]ms`
    // (invalid → 0), which silently kills the card transition on programmatic / fast swipes
    // (drag still felt smooth because every touchmove re-runs setTranslate, no transition needed).
    const setTransition = (_swiper, duration) => {
      swiper.slides.forEach((slide) => {
        const inner = slide.querySelector(".vertical-slideshow-card__inner");
        if (inner) inner.style.transitionDuration = `${duration}ms`;
      });
    };

    // Hook only `setTranslate` (not `progress`) — Swiper emits `progress` from inside its
    // setTranslate method and then emits `setTranslate` immediately after, so subscribing to
    // both makes our handler fire twice per tick and the second assignment can cancel the
    // CSS transition that just started on the first.
    swiper.on("setTranslate", setTranslate);
    swiper.on("setTransition", setTransition);
    setTranslate();
  };

  // Nav-pill arrow step (keyframes: `vertical-slideshow-arrow-step`). Restarting needs the class
  // dropped, a reflow, then re-added — re-adding it in the same frame is a no-op for the engine,
  // so an impatient second click would otherwise get no animation at all.
  const ARROW_STEPPING_CLASS = "is-stepping";

  const playArrowStep = (nav) => {
    if (prefersReducedMotion()) return;
    const icon = nav.querySelector(".icon");
    if (!icon) return;
    nav.classList.remove(ARROW_STEPPING_CLASS);
    void icon.offsetWidth;
    nav.classList.add(ARROW_STEPPING_CLASS);
  };

  const initNav = (box, swiper) => {
    if (!swiper) return;
    box.querySelectorAll(".vertical-slideshow__nav").forEach((nav) => {
      nav.addEventListener("click", () => {
        playArrowStep(nav);
        if (nav.dataset.direction === "next") {
          swiper.slideNext();
        } else {
          swiper.slidePrev();
        }
      });
      // Animation events bubble, so this catches the icon's own `animationend`.
      nav.addEventListener("animationend", () => {
        nav.classList.remove(ARROW_STEPPING_CLASS);
      });
    });
  };

  // Direction-aware clip-path wipe on the full-bleed backgrounds, synced to the card slider.
  // Each background carries `data-block-index` (the original block index, 0-based, NOT the
  // duplicated slide index) and the active slide carries the same attribute, so the mapping
  // survives our Liquid-side slide duplication for loop. Mirrors `initCreativeMedia` from
  // `featured-slideshow.js`, but:
  //   - backgrounds live outside the swiper (one per unique block) instead of per-slide
  //   - the wipe direction depends on swipe direction (see `revealsDownward`)
  const initBackgroundSync = (section, swiper) => {
    if (!swiper) return;
    const backgrounds = Array.from(
      section.querySelectorAll(".vertical-slideshow__background")
    );
    if (backgrounds.length < 2) return;

    const HIDDEN_BOTTOM = "inset(0 0 100% 0)"; // animates up to full → reveals top-to-bottom
    const HIDDEN_TOP = "inset(100% 0 0 0)"; // animates down to full → reveals bottom-to-top
    const SHOWN = "inset(0 0 0 0)";
    // Duration matches Swiper's speed (600ms) so the wipe finishes in lockstep with the slider
    // and doesn't trail behind on swipe.
    const WIPE = "clip-path 0.6s cubic-bezier(0.45, 0.75, 0.1, 0.9)";

    const setInstant = (bg, value) => {
      if (!bg) return;
      bg.style.transition = "none";
      bg.style.clipPath = value;
    };

    const getActiveBlockIndex = () => {
      const activeSlide = swiper.slides[swiper.activeIndex];
      if (!activeSlide) return null;
      const idx = Number(activeSlide.dataset.blockIndex);
      return Number.isNaN(idx) ? null : idx;
    };

    // Initial state: the block whose card is active is fully shown and on top of the stack;
    // everyone else is clipped away.
    const initialBlockIndex = getActiveBlockIndex() ?? 0;
    backgrounds.forEach((bg, i) => {
      setInstant(bg, i === initialBlockIndex ? SHOWN : HIDDEN_BOTTOM);
      bg.style.zIndex = i === initialBlockIndex ? "2" : "0";
      bg.classList.toggle("is-active", i === initialBlockIndex);
    });

    let lastBlockIndex = initialBlockIndex;
    const reduced = prefersReducedMotion();

    // On desktop the slider itself is vertical, so the wipe follows the cards' travel: next runs
    // downward. Below 990px the slider turns horizontal and there is no vertical travel to mirror,
    // so the wipe uses the theme's standard direction — next reveals upward, matching
    // `showcase-slideshow`'s HIDDEN_NEXT.
    const revealsDownward = (goingNext) =>
      desktopMql.matches ? goingNext : !goingNext;

    // Instantly show one block's background and hide the rest (reduced-motion + last-resort sync).
    const snapTo = (target) => {
      backgrounds.forEach((bg, i) => {
        setInstant(bg, i === target ? SHOWN : HIDDEN_BOTTOM);
        bg.style.zIndex = i === target ? "2" : "0";
        bg.classList.toggle("is-active", i === target);
      });
      lastBlockIndex = target;
    };

    // Animated clip-path wipe from the currently-shown block (`lastBlockIndex`, kept as the underlay)
    // to `target`; `goingNext` picks the wipe direction (down vs up).
    //
    // Stacking matters as much as clip-path: backgrounds are siblings, so later children paint on top
    // regardless of clip state. Without a dynamic z-index the outgoing underlay keeps covering the
    // incoming layer when its DOM order is later — the wipe runs but nothing shows. So promote the
    // incoming layer above the outgoing one.
    const wipeTo = (target, goingNext) => {
      const startClip = revealsDownward(goingNext) ? HIDDEN_BOTTOM : HIDDEN_TOP;
      backgrounds.forEach((bg, i) => {
        if (i === target) return;
        if (i === lastBlockIndex) {
          // Outgoing stays SHOWN underneath as the underlay during the wipe.
          setInstant(bg, SHOWN);
          bg.style.zIndex = "1";
          bg.classList.remove("is-active");
        } else {
          setInstant(bg, HIDDEN_BOTTOM);
          bg.style.zIndex = "0";
          bg.classList.remove("is-active");
        }
      });
      // Incoming: snap to the fully-clipped start, then animate to SHOWN. Double rAF waits for a real
      // paint of the start state — a single rAF can get batched with the end and skip the transition.
      const incoming = backgrounds[target];
      setInstant(incoming, startClip);
      incoming.style.zIndex = "2";
      incoming.classList.add("is-active");
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          incoming.style.transition = WIPE;
          incoming.style.clipPath = SHOWN;
        });
      });
      lastBlockIndex = target;
    };

    // Primary path: the committed slide change. Fires once when a transition kicks off (programmatic
    // slideTo, or a flick whose activeIndex changes on release) — never on mid-drag wiggles, so the
    // wipe doesn't restart while the finger hovers the threshold.
    swiper.on("slideChangeTransitionStart", () => {
      const newBlockIndex = getActiveBlockIndex();
      if (newBlockIndex === null || newBlockIndex === lastBlockIndex) return;
      if (reduced) {
        snapTo(newBlockIndex);
        return;
      }
      // Direction from swiper indices. With loop, a wrap (last → first) gives a delta whose abs
      // exceeds half the slide count; flip the inference there so the wipe matches the visual travel.
      const delta = swiper.activeIndex - swiper.previousIndex;
      let goingNext = delta > 0;
      if (Math.abs(delta) > swiper.slides.length / 2) goingNext = !goingNext;
      wipeTo(newBlockIndex, goingNext);
    });

    // Fallback path for changes `slideChangeTransitionStart` misses — mainly a slow / long drag:
    // Swiper updates `activeIndex` mid-drag as the wrapper passes the threshold, so by release the
    // change has already registered and the release-snap reports no change, firing no
    // `slideChangeTransition*` event. (Fast multi-slide flings + loop re-index can also leave the
    // index stale at the start event.) So on `touchEnd` (release) and the GENERIC `transitionEnd`
    // (after every snap) we reconcile against the ACTUAL DOM, deferred a frame so loopFix has landed:
    //   - if the active block's layer is already the only `is-active` one, a healthy wipe is in
    //     charge → leave it;
    //   - otherwise run the SAME animated wipe the primary path would have (direction from the
    //     gesture), so a long drag animates too instead of snapping.
    const reconcileBackground = () => {
      requestAnimationFrame(() => {
        // A release-snap may still be running: Swiper emits `touchEnd` BEFORE sliding to the final
        // index, so reconciling now wipes to the overshoot the drag merely passed through. That
        // layer then becomes the underlay of the snap's own wipe and flashes full-screen mid-
        // animation — a background one step beyond the one being scrolled to. The snap's
        // `transitionEnd` reconciles once the index is final.
        if (swiper.animating) return;
        const idx = getActiveBlockIndex();
        if (idx === null) return;
        const target = backgrounds[idx];
        const alreadyCorrect =
          target &&
          target.classList.contains("is-active") &&
          backgrounds.every(
            (bg, i) => i === idx || !bg.classList.contains("is-active")
          );
        if (alreadyCorrect) return;
        if (reduced) {
          snapTo(idx);
          return;
        }
        const goingNext = swiper.swipeDirection
          ? swiper.swipeDirection === "next"
          : true;
        wipeTo(idx, goingNext);
      });
    };
    swiper.on("transitionEnd", reconcileBackground);
    swiper.on("touchEnd", reconcileBackground);
  };

  // Keep Tab-navigation focused on the *visible* centred card only. Peeking neighbours are
  // links too, so by default Tab would land on each of them and the page would scroll/focus
  // in unintuitive ways. Setting `tabindex="-1"` on non-active links takes them out of the
  // sequential focus order; the active card stays normally focusable. `<div>` cards (no link)
  // aren't focusable anyway, so we skip them.
  const initFocusManagement = (swiper) => {
    if (!swiper) return;
    const update = () => {
      swiper.slides.forEach((slide) => {
        const card = slide.querySelector(".vertical-slideshow-card");
        if (!card || card.tagName !== "A") return;
        if (slide.classList.contains("swiper-slide-active")) {
          card.removeAttribute("tabindex");
        } else {
          card.setAttribute("tabindex", "-1");
        }
      });
    };
    // `slideChangeTransitionEnd` fires after the transition has fully committed — by then
    // `.swiper-slide-active` is on the final active slide, so the tabindex shuffle lands on
    // the right targets even if Swiper's loop logic rearranged things mid-transition.
    swiper.on("slideChangeTransitionEnd", update);
    update();
  };

  const initSection = (section) => {
    if (!section || !section.classList.contains("vertical-slideshow-section")) {
      return;
    }

    const box = section.querySelector(".vertical-slideshow");
    if (!box) return;

    const swiper = initSwiper(box);
    initEffect(swiper);
    initNav(box, swiper);
    initBackgroundSync(box, swiper);
    initFocusManagement(swiper);
  };

  // Defer the whole setup until the section approaches the viewport (400px ahead). The observer
  // fires immediately for sections already on screen, so above-the-fold instances initialize
  // right away; below-the-fold ones keep their Swiper init cost out of the page-load window.
  const lazyInitSection = (section) => {
    if (!section || !section.classList.contains("vertical-slideshow-section")) {
      return;
    }
    // The theme editor re-executes this script on every section re-render, so a fresh section
    // gets init calls from BOTH the re-run's direct call and the `shopify:section:load` listener.
    // A second init would stack another Swiper + nav clicks + background-wipe handlers on the
    // same DOM (multi-slide jumps per click, wipes fighting over clip-path/z-index).
    if (section.dataset.vsInitialized) return;
    section.dataset.vsInitialized = "true";
    if (typeof IntersectionObserver !== "function") {
      initSection(section);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        initSection(section);
      },
      { rootMargin: "400px 0px" }
    );
    observer.observe(section);
  };

  lazyInitSection(document.currentScript.parentElement);

  // Bind the document listener exactly once (same pattern as featured-products.js): editor
  // re-executions would otherwise accumulate listeners on the persistent `document`, and each
  // section reload would then run one extra init per accumulated listener. The closure is
  // stateless — it only touches the event target's fresh DOM — so keeping the first run's
  // function is safe.
  if (!window.__verticalSlideshowBound) {
    window.__verticalSlideshowBound = true;
    document.addEventListener("shopify:section:load", function (event) {
      lazyInitSection(event.target);
    });
  }
})();
